import {
  addDays,
  addSeconds,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInDays,
  differenceInMinutes,
  hoursToSeconds,
  intervalToDuration,
  isBefore,
  secondsToHours,
  secondsToMinutes,
  subDays,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { pluralize } from '../string-i18n/string.i18n.utils';

import { formatDate, formatTime, getDaysAgo, isDateBetweenDates, isDateInThePast, toDate } from './date.utils';

const toOsloTimezone = (date: Date | string) => toZonedTime(new Date(date), 'Europe/Oslo');

/**
 * @description If toDateTime is more/less than 7 days from now:
 * Returns a date rounded to closest full week so that x.5 weeks => x+1
 */
function roundWeeks(toDateTime: Date | string) {
  const diff = differenceInCalendarDays(toDate(toDateTime), new Date());
  const absoluteDiff = Math.abs(diff);
  const daysIntoWeek = absoluteDiff % 7;
  const to: Date = toDate(toDateTime);

  if (absoluteDiff > 7 && daysIntoWeek > 3) {
    const daysFromCompleteWeek = 7 - daysIntoWeek; // Append/subtract days so that one and a half weeks gets formatted as two weeks
    if (diff > 0) {
      // Future
      return addDays(to, daysFromCompleteWeek);
    } else {
      // Past
      return subDays(to, daysFromCompleteWeek);
    }
  }
  return to;
}

export const readableDuration = (durationSeconds: number) => {
  const hours = secondsToHours(durationSeconds);
  const durationSubHours = durationSeconds - hoursToSeconds(hours);
  const minutes = secondsToMinutes(durationSubHours);
  const hoursText = `${hours} ${pluralize(hours, 'time', 'timer')}`;
  const minutesText = `${minutes} ${pluralize(minutes, 'minutt', 'minutter')}`;
  if (hours > 0 && minutes > 0) {
    return `${hoursText} og ${minutesText}`;
  } else if (hours > 0) {
    return hoursText;
  } else if (minutes > 0) {
    return minutesText;
  } else {
    return '';
  }
};

export const formatPublishedToUTC = (availableTo: Date) => {
  const roundedDate = roundWeeks(toOsloTimezone(availableTo));
  const prefix = differenceInMinutes(roundedDate, new Date()) <= 60 ? 'Tilgjengelig til ' : 'Tilgjengelig i ';
  const interval = intervalToDuration({
    start: toOsloTimezone(new Date()),
    end: roundedDate,
  });
  const { years = 0, months = 0, days = 0, hours = 0 } = interval;

  if (isBefore(roundedDate, new Date())) {
    return null;
  }
  if (years > 0) {
    return `${prefix}${years} år`.trim();
  }
  if (months > 0) {
    return `${prefix}${pluralizeWithNumber(months, 'måned', 'måneder')}`.trim();
  }
  if (days > 0) {
    const weeks = Math.floor(days / 7);
    if (weeks >= 1) {
      return `${prefix}${pluralizeWithNumber(weeks, 'uke', 'uker')}`.trim();
    }
    return `${prefix}${pluralizeWithNumber(days, 'dag', 'dager')}`.trim();
  }
  if (hours > 0) {
    return `${prefix}${pluralizeWithNumber(hours, 'time', 'timer')}`.trim();
  }

  return `${prefix}${formatTime(roundedDate)}`;
};

export const formatBroadcastTime = (
  broadcastTime: string | Date,
  { withTime = true, duration = 0, useBroadcastDate = true } = {}
): string => {
  if (broadcastTime == null) {
    return '';
  }
  // Spec: https://www.figma.com/file/LcLOAwpVcKzhHUlKq4h6jI/Rules-and-regulations?node-id=1%3A958

  const now = Date.now();
  const localTime = toOsloTimezone(broadcastTime);
  const localTimeEnd = duration && addSeconds(localTime, duration);
  // check if is live now
  if (localTimeEnd && isDateBetweenDates(new Date(now), localTime, localTimeEnd)) {
    return `nå: ${formatTime(localTime)} - ${formatTime(localTimeEnd)}`;
  }

  const dayDiff = getDaysAgo(localTime, useBroadcastDate);
  const monthDiff = Math.abs(differenceInCalendarMonths(Date.now(), localTime));

  let daysAgoFormatted = '';
  if (dayDiff === 0) {
    daysAgoFormatted = 'i dag kl.';
  } else if (dayDiff === 1) {
    daysAgoFormatted = 'i går kl.';
  } else if (dayDiff === -1) {
    daysAgoFormatted = 'i morgen kl.';
  } else if (Math.abs(dayDiff) < 7) {
    const format = withTime ? "iiii 'kl.' HH:mm" : 'iiii';
    return formatDate(localTime, format);
  } else if (monthDiff >= 11) {
    return formatDate(localTime, 'd. MMM yyyy');
  } else if (monthDiff > 0) {
    return formatDate(localTime, 'd. MMM');
  } else {
    daysAgoFormatted = formatDate(toDate(localTime), "iiii d. MMM 'kl.'");
  }

  const dateAndTime = [daysAgoFormatted, formatTime(localTime)];
  return withTime ? dateAndTime.join(' ') : daysAgoFormatted;
};

export const formatBroadcastDateWithPrefix = (datetime?: Date | string | null, duration = 0): string => {
  if (!datetime) {
    return '';
  }
  // check program end time to get correct prefix
  const prefix = isDateInThePast(addSeconds(toOsloTimezone(datetime), duration)) ? 'Sendt' : 'Sendes';
  return `${prefix} ${formatBroadcastTime(datetime, { duration })}`;
};

const pluralizeWithNumber = (number: number, postfix: string, pluralPostfix: string) => {
  if (number === 0) {
    return '';
  }
  return `${number} ${pluralize(number, postfix, pluralPostfix)}`;
};

export const getDaysOfTrailLeft = (trailEndDate: Date, now = new Date()) => {
  const daysInDifference = differenceInDays(trailEndDate, now);

  if (daysInDifference <= 0) {
    return undefined;
  }

  const weeksInDifference = Math.floor(daysInDifference / 7);
  const plusWeeks = pluralizeWithNumber(weeksInDifference, 'uke', 'uker') || '';
  const plusDays = pluralizeWithNumber(daysInDifference - weeksInDifference * 7, 'dag', 'dager');

  if (plusWeeks !== '' || plusDays !== '') {
    return `Du har ${plusWeeks}${
      !plusWeeks ? plusDays : `${plusDays ? ` og ${plusDays}` : ''}`
    } igjen av prøveperioden din.`;
  }
};
